import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { getProductById, updateProduct, deleteProductFile } from '../services/productApi';
import { uploadProductFiles, clearPendingUpload } from '../services/chunkedUpload';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { API_BASES } from '../config';
import { IMG_FALLBACK, onImgError } from '../utils/imageFallback';
import '../styles/ProgressBar.css';


const FILES_BASE = API_BASES.files;

// Límites de tamaño alineados con UploadProductPage
const MAX_FILE_SIZE_MB = 1000; // 1 GB (archivos del producto)
const MAX_PHOTO_SIZE_MB = 5;   // 5 MB (imágenes)

export default function ProductDetailEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, role, username } = useContext(AuthContext);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deletingId, setDeletingId] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    nombre: '',
    descripcionProd: '',
    precioIndividual: '',
    pais: '',
    categorias: [],
    especialidades: [],
    existingPhotos: [],
    newPhotos: [],
    existingAutFiles: [],
    newAutFiles: [],
  });

  const isApproved = product?.estado === 'APROBADO';
  const isRejected = product?.estado === 'RECHAZADO' || product?.estado === 'REJECTED';
  const canEditAllFields = !isApproved && !isRejected;
  const isPendingProduct = product?.estado === 'PENDIENTE';


  // Image Gallery & Zoom
  const [selectedIndex, setSelectedIndex] = useState(0);
  const imgWrapperRef = useRef(null);
  const [zoomStyle, setZoomStyle] = useState({ transform: 'scale(1)', transformOrigin: 'center center' });

  const selectImage = (i) => {
    setSelectedIndex(i);
    setZoomStyle({ transform: 'scale(1)', transformOrigin: 'center center' });
  };

  const onMouseMove = e => {
    if (!imgWrapperRef.current) return;
    const { left, top, width, height } = imgWrapperRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({ transform: 'scale(1.5)', transformOrigin: `${x}% ${y}%` });
  };
  const onMouseLeave = () => setZoomStyle({ transform: 'scale(1)', transformOrigin: 'center center' });

  // Previews for new photos
  const newPhotoPreviews = useMemo(
    () => formData.newPhotos.map(f => URL.createObjectURL(f)),
    [formData.newPhotos]
  );

  const previewsRef = useRef([]);
  previewsRef.current = newPhotoPreviews;
  useEffect(() => () => {
    previewsRef.current.forEach(url => URL.revokeObjectURL(url));
  }, []);

  const allImageUrls = useMemo(() => {
    const existingUrls = formData.existingPhotos.map(p => p.url);
    const newUrls = newPhotoPreviews;
    return [...existingUrls, ...newUrls];
  }, [formData.existingPhotos, newPhotoPreviews]);

  useEffect(() => {
    // Los archivos nuevos se suben al grupo que ya tiene el producto en file-service.
    clearPendingUpload();
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getProductById(id)
      .then(res => {
        const d = res.data;
        if (!d) {
          setError('No se encontraron datos del producto.');
          return;
        }

        // Las URLs mandan: en PENDIENTE los ids pueden venir vacíos (archivos en staging).
        const ids = Array.isArray(d.fotografiaProd) ? d.fotografiaProd : [];
        const urls = Array.isArray(d.fotografiaUrl) ? d.fotografiaUrl : [];
        const existingPhotos = urls.map((url, idx) => {
          const driveId = ids[idx] || null;
          return {
            id: driveId || `staging-${idx}`,
            driveId,
            url: url || `${FILES_BASE}/${d.idProducto}/${driveId}`,
          };
        });

        // Existing AUT Files
        const autIds = Array.isArray(d.archivosAut) ? d.archivosAut : [];
        const autUrls = Array.isArray(d.archivosAutUrls) ? d.archivosAutUrls : [];
        const autNames = Array.isArray(d.archivosAutNombres) ? d.archivosAutNombres : [];
        const existingAutFiles = autUrls.map((u, idx) => ({
          url: u,
          driveId: autIds[idx] || null,
          name: autNames[idx] || (typeof u === 'string' ? u.split('/').pop() : null) || 'archivo'
        }));

        setProduct(d);
        setSelectedIndex(0);
        setFormData(prev => ({
          ...prev,
          nombre: d.nombre || '',
          descripcionProd: d.descripcionProd || '',
          precioIndividual: d.precioIndividual ?? '',
          pais: d.pais || '',
          categorias: d.categorias || [],
          especialidades: d.especialidades || [],
          existingPhotos,
          newPhotos: [],
          existingAutFiles,
          newAutFiles: [],
        }));
      })
      .catch(err => {
        console.error("Error cargando producto para editar:", err);
        setError("Error al cargar los datos del producto.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNewPhotosChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // 1. Límite individual por imagen
    const individualTooLarge = files.some(f => f.size > MAX_PHOTO_SIZE_MB * 1024 * 1024);
    if (individualTooLarge) {
      toast.error(`Una de tus imágenes supera el límite individual de ${MAX_PHOTO_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }

    // 2. Límite acumulado de las nuevas imágenes
    const currentSize = formData.newPhotos.reduce((acc, f) => acc + f.size, 0);
    const incomingSize = files.reduce((acc, f) => acc + f.size, 0);
    if ((currentSize + incomingSize) > MAX_PHOTO_SIZE_MB * 1024 * 1024) {
      toast.error('Tus imágenes superan el límite permitido.');
      e.target.value = '';
      return;
    }

    setFormData(prev => ({
      ...prev,
      newPhotos: [...prev.newPhotos, ...files],
    }));
    e.target.value = '';
  };

  // En PENDIENTE el borrado debe tocar Drive: el staging del grupo se promociona al aprobar.
  const removeExistingPhoto = async (photo) => {
    if (isPendingProduct && photo.driveId) {
      setDeletingId(photo.id);
      try {
        await deleteProductFile(product.idProducto, photo.driveId);
      } catch (err) {
        console.error("Error eliminando foto en staging:", err);
        toast.error("No se pudo eliminar la foto. Inténtalo de nuevo.");
        return;
      } finally {
        setDeletingId(null);
      }
    }
    setFormData(prev => ({
      ...prev,
      existingPhotos: prev.existingPhotos.filter(p => p.id !== photo.id),
    }));
    setSelectedIndex(0);
    toast.info("Foto eliminada.");
  };

  const removeNewPhoto = (idx) => {
    setFormData(prev => {
      const next = [...prev.newPhotos];
      next.splice(idx, 1);
      return { ...prev, newPhotos: next };
    });
  };

  const handleNewAutFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // 1. Límite individual por archivo
    const individualTooLarge = files.some(f => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (individualTooLarge) {
      toast.error(`Uno de tus archivos supera el límite individual de ${MAX_FILE_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }

    // 2. Límite acumulado de los nuevos archivos
    const currentSize = formData.newAutFiles.reduce((acc, f) => acc + f.size, 0);
    const incomingSize = files.reduce((acc, f) => acc + f.size, 0);
    if ((currentSize + incomingSize) > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error('Tus archivos superan el límite permitido.');
      e.target.value = '';
      return;
    }

    setFormData(prev => ({
      ...prev,
      newAutFiles: [...prev.newAutFiles, ...files],
    }));
    e.target.value = '';
  };

  const removeExistingAut = async (autFile) => {
    if (isPendingProduct && autFile.driveId) {
      setDeletingId(autFile.url);
      try {
        await deleteProductFile(product.idProducto, autFile.driveId);
      } catch (err) {
        console.error("Error eliminando archivo en staging:", err);
        toast.error("No se pudo eliminar el archivo. Inténtalo de nuevo.");
        return;
      } finally {
        setDeletingId(null);
      }
    }
    setFormData(prev => ({
      ...prev,
      existingAutFiles: prev.existingAutFiles.filter(f => f.url !== autFile.url),
    }));
    toast.info("Archivo eliminado.");
  };

  const removeNewAutFile = (idx) => {
    setFormData(prev => {
      const next = [...prev.newAutFiles];
      next.splice(idx, 1);
      return { ...prev, newAutFiles: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRejected) {
      toast.error('Producto rechazado: no se puede editar.');
      setIsSaving(false);
      return;
    }
    setIsSaving(true);

    // En PENDIENTE los archivos nuevos van por subida reanudable (chunks).
    const newFiles = [...formData.newPhotos, ...formData.newAutFiles];
    const useChunked = !isApproved && newFiles.length > 0;

    try {
      setProgress(0);
      let groupKey = null;
      if (useChunked) {
        ({ groupKey } = await uploadProductFiles(newFiles, {
          onProgress: setProgress,
          productId: product?.idProducto,
        }));
      }

      const dto = {
        idProducto: product?.idProducto,
        nombre: formData.nombre,
        descripcionProd: formData.descripcionProd,
        precioIndividual: Number(formData.precioIndividual),
        pais: canEditAllFields ? formData.pais : product.pais,
        categorias: canEditAllFields ? formData.categorias : product.categorias,
        especialidades: canEditAllFields ? formData.especialidades : product.especialidades,
        groupKey: groupKey || undefined,
      };

      const dataToSend = new FormData();
      dataToSend.append('dto', new Blob([JSON.stringify(dto)], { type: 'application/json' }));

      dataToSend.append('keepFotoIds', JSON.stringify(formData.existingPhotos.map(p => p.driveId).filter(Boolean)));

      if (isApproved) {
        formData.newPhotos.forEach(f => dataToSend.append('fotos', f));
      } else {
        dataToSend.append('autKeepUrls', JSON.stringify(formData.existingAutFiles.map(f => f.url)));
        if (!useChunked) {
          formData.newPhotos.forEach(f => dataToSend.append('fotos', f));
          formData.newAutFiles.forEach(f => dataToSend.append('archivosAut', f));
        }
      }

      await updateProduct(id, dataToSend);
      if (useChunked) clearPendingUpload();
      toast.success("Producto actualizado exitosamente!");
      navigate('/mis-productos');
    } catch (err) {
      console.error("Error al actualizar producto:", err);
      toast.error("Error al actualizar el producto. " + (err.response?.data?.message || err.message));
    } finally {
      newPhotoPreviews.forEach(url => URL.revokeObjectURL(url));
      setIsSaving(false);
      setProgress(0);
    }
  };

  const canEdit = isAuthenticated && role === 'ROL_COLABORADOR' && (!!username ? product?.uploaderUsername === username : true) && !isRejected;

  const fmtSize = (bytes) => {
    if (bytes == null) return '';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    const kb = bytes / 1024;
    return `${kb.toFixed(0)} KB`;
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status" className="aec-spinner">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>Volver</Button>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container className="my-5">
        <Alert variant="info">Producto no encontrado.</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>Volver</Button>
      </Container>
    );
  }

  if (!canEdit) {
    return (
      <Container className="my-5">
        <Alert variant="danger">No tienes permiso para editar este producto.</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>Volver</Button>
      </Container>
    );
  }

  if (isRejected) {
    return (
      <Container className="my-5">
        <Alert variant="danger">Este producto fue RECHAZADO y no puede editarse.</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>Volver</Button>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <Card className="shadow-sm overflow-hidden">
        <Card.Body className="p-4 p-md-5">
          <Row className="g-4">
            {/* Columna de imágenes con zoom y gestión de fotos */}
            <Col xs={12} md={6} lg={5} className="d-flex flex-column align-items-center">
              <div
                ref={imgWrapperRef}
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
                className="product-image-wrapper rounded position-relative"
                style={{ width: '100%', maxWidth: 520 }}
              >
                <img
                  src={allImageUrls[selectedIndex] || IMG_FALLBACK}
                  alt={product?.nombre || 'Product Image'}
                  className="product-detail-img"
                  style={{ width: '100%', height: 'auto', borderRadius: 8, ...zoomStyle }}
                  onError={onImgError}
                />
              </div>

              {/* Miniaturas existentes */}
              {allImageUrls.length > 0 && (
                <div className="thumbs-strip mt-3 d-flex flex-wrap gap-2 justify-content-center">
                  {formData.existingPhotos.map((p, i) => (
                    <div key={p.id} className="text-center">
                      <button
                        type="button"
                        className={`thumb-btn ${i === selectedIndex ? 'active' : ''}`}
                        onClick={() => selectImage(i)}
                        title={`Imagen ${i + 1}`}
                        style={{ border: 'none', background: 'transparent' }}
                      >
                        <img
                          src={p.url}
                          alt={`Miniatura ${i + 1}`}
                          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6 }}
                          onError={onImgError}
                        />
                      </button>
                      {/* Botón para eliminar foto existente */}
                      <div className="form-check mt-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeExistingPhoto(p)}
                          disabled={deletingId === p.id}
                        >
                          {deletingId === p.id ? 'Eliminando…' : 'Eliminar'}
                        </button>
                      </div>
                    </div>
                  ))}
                  {newPhotoPreviews.map((src, i) => (
                    <div key={i} className="text-center">
                      <button
                        type="button"
                        className={`thumb-btn ${i + formData.existingPhotos.length === selectedIndex ? 'active' : ''}`}
                        onClick={() => selectImage(i + formData.existingPhotos.length)}
                        title={`Nueva Imagen ${i + 1}`}
                        style={{ border: 'none', background: 'transparent' }}
                      >
                        <img
                          src={src}
                          alt={`Nueva ${i + 1}`}
                          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6 }}
                        />
                      </button>
                      {/* Botón para quitar nueva foto */}
                      <div className="form-check mt-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeNewPhoto(i)}
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Nuevas fotos */}
              <Form.Group className="mt-3 w-100">
                <Form.Label>Agregar nuevas fotos</Form.Label>
                <Form.Control type="file" multiple accept="image/*" onChange={handleNewPhotosChange} />
                {formData.newPhotos.length > 0 && (
                  <small className="text-muted d-block mt-1">
                    {formData.newPhotos.length} archivo(s) seleccionado(s)
                  </small>
                )}
              </Form.Group>
            </Col>

            {/* Columna de formulario de edición */}
            <Col xs={12} md={6} lg={7}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h1 className="fw-bold m-0">Editar ficha</h1>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" onClick={() => navigate(`/mis-productos`)}>
                    Cancelar
                  </Button>
                  <Button variant="success" onClick={handleSubmit} disabled={isSaving || loading}>
                    {isSaving ? 'Guardando…' : 'Guardar cambios'}
                  </Button>
                </div>
              </div>

              {isSaving && (
                <div className="aec-progress aec-progress--fluid">
                  <ProgressBar
                    now={progress || 100}
                    striped
                    animated
                    label={progress ? `${progress}%` : 'Guardando…'}
                  />
                </div>
              )}

              {isApproved && (
                <Alert variant="info" className="mb-4">
                  Este producto está APROBADO. Solo puedes editar el título, descripción, precio y fotografías.
                </Alert>
              )}
              {!canEditAllFields && !isApproved && (
                <Alert variant="warning" className="mb-4">
                  Solo puedes editar título, descripción, precio y fotografías de productos <strong>APROBADOS</strong> que te pertenecen.
                </Alert>
              )}

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Título</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleBasicChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Precio</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="precioIndividual"
                    value={formData.precioIndividual}
                    onChange={handleBasicChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={8}
                    name="descripcionProd"
                    value={formData.descripcionProd}
                    onChange={handleBasicChange}
                    required
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                  <Form.Text className="text-muted">
                    Se respetarán saltos de línea y espacios (pre-wrap).
                  </Form.Text>
                </Form.Group>

                {/* Archivos AUT (Deshabilitados si está APROBADO) */}
                <div className="mb-3">
                  <label className="form-label d-block">Archivos del producto actuales</label>
                  {isApproved && <small className="text-muted d-block mb-2">No se pueden modificar archivos en un producto aprobado.</small>}
                  {formData.existingAutFiles.length === 0 ? (
                    <small className="text-muted">No hay archivos actuales.</small>
                  ) : (
                    <ul className="list-group">
                      {formData.existingAutFiles.map((f) => (
                        <li key={f.url} className="list-group-item d-flex justify-content-between align-items-center">
                          <a href={f.url} target="_blank" rel="noopener noreferrer">{f.name}</a>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeExistingAut(f)}
                            disabled={isApproved || deletingId === f.url}
                          >
                            {deletingId === f.url ? 'Eliminando…' : 'Eliminar'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="archivosAut" className="form-label">Agregar archivos del producto (múltiples)</label>
                  <input
                    type="file"
                    className="form-control"
                    id="archivosAut"
                    name="archivosAut"
                    multiple
                    onChange={handleNewAutFilesChange}
                    disabled={isApproved}
                  />
                  {formData.newAutFiles.length > 0 && (
                    <ul className="list-group mt-2">
                      {formData.newAutFiles.map((file, idx) => (
                        <li key={`${file.name}-${idx}`} className="list-group-item d-flex justify-content-between align-items-center">
                          <span>{file.name} <small className="text-muted">({fmtSize(file.size)})</small></span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeNewAutFile(idx)}
                            disabled={isSaving}
                          >
                            Quitar
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-4">
                  <div className="text-secondary">Creador: <strong className="text-dark">{product?.uploaderUsername || 'Desconocido'}</strong></div>
                  <div className="text-secondary">Estado: <strong className="text-dark">{product?.estado}</strong></div>
                </div>
              </Form>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}